import { h, Component, Host, State, Element, Prop, Listen, Fragment, Method, Watch } from "@stencil/core";
import { StickyLevel } from "./constants/navigation-types";
import pfx from "../../utils/style-class-prefix-helper";
import { KeyIds } from "../../constants/keys";
import { BurgerPosition } from "./navigation-components/navigation-main/constants/navigation-main-types";
import { NAVIGATION_MAIN_BURGERPOSITION_LEFT } from "./navigation-components/navigation-main/constants/navigation-main-constants";
import { setCssVariable } from "../../utils/css-variables";
import { isTouchDevice } from "../../utils/touch-helper";
import {
  NAVIGATION_STICKYSLEVEL_MAIN,
  NAVIGATION_STICKYSLEVEL_META,
  NAVIGATION_STICKYSLEVEL_TOOLBAR
} from "./constants/navigation-constants";
import {
  NAVIGATION_TOOLBAR_FLYOUT_INNER_BACKGROUND,
  NAVIGATION_MAIN_FLYOUT_INNER_BACKGROUND,
  NAVIGATION_MOBILE_FLYOUT_INNER_BACKGROUND
} from "../../constants/global-constants";
import { StyledComponent } from "../../decorators/styled-component";
import sanitizeHtml from "sanitize-html";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [meta] - holds eon-ui-meta-navigation.
 * @slot [toolbar] - holds eon-ui-toolbar-navigation.
 * @slot [main] - holds eon-ui-meta-navigation.
 * @slot [extension] - holds any further navigation and puts it to the bottom of the container.
 */
@Component({
  tag: "eon-ui-navigation",
  styleUrl: "navigation.scss",
  shadow: true
})
export class Navigation {
  // @InitLogging() sentryTransaction;
  metaHeight = 46;
  toolbarHeight = 72;
  mainHeight = 64;

  scrollTimeout;
  resizeTimeout;
  mainWrapperTimeout;
  toolbarWrapperTimeout;
  mouseLeaveTimeout;

  lastScrollTop = 0;

  hasMetaNavigation: boolean;
  hasToolbarNavigation: boolean;
  hasMainNavigation: boolean;
  hasExtension: boolean;

  toolbarNavigation;
  toolbarChilds;
  activeToolbarSubContainer;
  toolbarLinks;

  mainNavigation;
  mainChilds;
  mainStandardChilds;
  mainIconChilds;
  mainLinks;
  mainIconLinks;
  mainSubWrapper;
  activeMainSubContainer;

  burgerPosition: BurgerPosition = NAVIGATION_MAIN_BURGERPOSITION_LEFT;
  mobileSubChilds;
  activeMobileSubContainer;

  mobileNavigationPadding = 0;
  mobileNavigationPaddingSticky = 0;

  @State() currentScrollDirection: string = "up";
  @State() isToolbarSubOpen: boolean = false;
  @State() toolbarSubHeight: number;
  @State() isMainSubWrapperOpen: boolean = false;
  @State() isMainSubOpen: boolean = false;
  @State() mainSubHeight: number;
  @State() navigationWrapper: HTMLElement;
  @State() mobileNavigation: HTMLElement;
  @State() mobileSubNavigation: HTMLElement;
  @State() mobileWrapper: HTMLElement;
  @State() mobileMainNavigation: HTMLElement;
  @State() isMobileNavOpen: boolean = false;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiNavigationElement;

  /**
   * Activate fixed positioning and sticky behaviour.
   */
  @Prop() sticky: boolean = true;

  /**
   * Defines which navigation sections should stay visible when scrolling down.
   */
  @Prop() stickyLevel: StickyLevel;

  /**
   * Method to evaluate the component height
   */
  @Method()
  async updateNavigationHeight() {
    // We could not use this.hostElement because if sticky-true, the height is 0
    // navigationWrapper delivers always the height, independent of sticky
    return await this.navigationWrapper.getBoundingClientRect().height;
  }

  /**
   * Open toolbar sub navigation wrapper and show related part
   */
  @Method()
  async openToolbarSub(rel: string, keyboard: boolean = false) {
    clearTimeout(this.toolbarWrapperTimeout);
    this.deactivateToolbarLinks();
    this.closeMainSub();
    this.isMobileNavOpen && this.closeMobileNav();
    this.isMainSubOpen && this.closeMainSub();
    this.activeToolbarSubContainer = this.hostElement.querySelector(`#${rel}`) as HTMLElement;

    if (this.activeToolbarSubContainer) {
      this.isToolbarSubOpen = true;
      this.showToolbarSub();
      if (!!keyboard) this.initSubKeyboardNav(this.activeToolbarSubContainer);
    }
  }

  /**
   * Close toolbar sub navigation wrapper
   */
  @Method()
  async closeToolbarSub() {
    clearTimeout(this.toolbarWrapperTimeout);
    this.deactivateToolbarLinks();
    this.isToolbarSubOpen = false;
    this.toolbarWrapperTimeout = setTimeout(() => {
      if (this.activeToolbarSubContainer) this.activeToolbarSubContainer.style.display = "none";
    }, 800);
  }

  /**
   * Open main sub navigation wrapper and show related part
   */
  @Method()
  async openMainSub(rel: string, keyboard: boolean = false) {
    clearTimeout(this.mainWrapperTimeout);
    this.deactivateMainLinks();
    this.deactivateMainIconLinks();
    this.isMobileNavOpen && this.closeMobileNav();
    this.isToolbarSubOpen && this.closeToolbarSub();
    this.activeMainSubContainer = this.hostElement.querySelector(`#${rel}`) as HTMLElement;

    if (this.activeMainSubContainer) {
      this.isMainSubOpen = true;
      this.isMainSubWrapperOpen = true;
      this.showMainSub();
      if (!!keyboard) this.initSubKeyboardNav(this.activeMainSubContainer);
    }
  }

  /**
   * Close main sub navigation wrapper
   */
  @Method()
  async closeMainSub() {
    clearTimeout(this.mainWrapperTimeout);
    this.deactivateMainLinks();
    this.deactivateMainIconLinks();
    this.isMainSubOpen = false;
    this.mainWrapperTimeout = setTimeout(() => {
      this.isMainSubWrapperOpen = false;
      if (this.activeMainSubContainer) this.activeMainSubContainer.style.display = "none";
    }, 400);
  }

  /**
   * Open mobile nav
   */
  @Method()
  async openMobileNav() {
    this.closeMobileSub();
    this.isMainSubOpen && this.closeMainSub();
    this.isToolbarSubOpen && this.closeToolbarSub();
    this.mobileWrapper.classList.add("act");
    this.isMobileNavOpen = true;
    this.mainNavigation.burgerActive = true;
  }

  /**
   * Close mobile nav
   */
  @Method()
  async closeMobileNav() {
    this.deactivateMainIconLinks();
    this.mobileWrapper.classList.remove("act");
    this.isMobileNavOpen = false;
    this.mainNavigation.burgerActive = false;
    setTimeout(() => {
      this.closeMobileSub();
    }, 400);
  }

  /**
   * Open mobile sub navigation wrapper and show related part
   */
  @Method()
  async openMobileSub(rel: string) {
    this.activeMobileSubContainer = this.mobileWrapper.querySelector(`#mobile-${rel}`) as HTMLElement;
    this.showMobileSub();
  }

  /**
   * Open mobile sub navigation wrapper and show related part
   */
  @Method()
  async openMobileSubImmediately(rel: string) {
    this.mainNavigation.burgerActive = false;
    this.closeMobileSub();
    this.activeMobileSubContainer = this.mobileWrapper.querySelector(`#mobile-${rel}`) as HTMLElement;
    this.isMainSubOpen && this.closeMainSub();
    this.isToolbarSubOpen && this.closeToolbarSub();
    this.mobileWrapper.classList.add("act");
    this.isMobileNavOpen = true;
    this.showMobileSubImmediately();
  }

  /**
   * Close mobile sub navigation wrapper
   */
  @Method()
  async closeMobileSub() {
    this.deactivateMainIconLinks();
    this.mobileSubChilds.forEach((element: HTMLElement) => {
      element.classList.remove("act");
      element.querySelector(".mobile-secondary-nav-inner").scrollTop = 0;
    });
    this.mobileMainNavigation.scrollTop = 0;
    this.mobileMainNavigation.classList.remove("scale"); //why does this even work?
  }

  @Listen("scroll", { target: "window" })
  detectScrollDirection() {
    if (this.sticky) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => {
        if (!isTouchDevice()) {
          let st = window.pageYOffset || document.documentElement.scrollTop;
          if (st > 128 && st > this.lastScrollTop) {
            this.currentScrollDirection = "down";
          } else {
            this.currentScrollDirection = "up";
          }

          this.lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling
        } else {
          if (document.documentElement.scrollTop > 128) {
            this.currentScrollDirection = "down";
          } else {
            setTimeout(() => {
              this.currentScrollDirection = "up";
            }, 500);
          }
        }
      }, 50);
    }
  }

  @Listen("resize", { target: "window" })
  closeNavigationsOnResize() {
    if (this.isToolbarSubOpen || this.isMainSubOpen) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.closeToolbarSub();
        this.closeMainSub();
      }, 100);
    }
  }

  componentWillLoad() {
    this.getNavigationStructure();
    this.calcMobileNavigationPadding();
    this.toolbarNavigation = this.hostElement.querySelector('[slot="toolbar"]');
    this.toolbarChilds = this.hostElement.querySelectorAll('[slot="toolbar-sub"]');
    this.toolbarLinks = this.hostElement.querySelectorAll("eon-ui-navigation-toolbar-icon-link");
    this.mainNavigation = this.hostElement.querySelector('[slot="main"]');
    this.mainChilds = this.hostElement.querySelectorAll('[slot="main-sub"], [slot="main-icon-sub"]');
    this.mainStandardChilds = this.hostElement.querySelectorAll('[slot="main-sub"]');
    this.mainIconChilds = this.hostElement.querySelectorAll('[slot="main-icon-sub"]');
    this.mainLinks = this.hostElement.querySelectorAll("eon-ui-navigation-main-link");
    this.mainIconLinks = this.hostElement.querySelectorAll("eon-ui-navigation-main-icon-link");
    this.setParentNavTypeDataAttributeToFlyoutLink();
    this.setInnerBackgroundToSubs();
  }

  componentDidLoad() {
    this.mainSubWrapper && this.initMainSubFunctionality();
    this.createMainMobileNavigation();
    this.mobileSubChilds = this.mobileWrapper?.querySelectorAll(".mobile-secondary-nav");
    this.setCssVariables();
  }

  /**
   * Adds type of parent navigation to flyout link. This is needed for tracking.
   */
  setParentNavTypeDataAttributeToFlyoutLink() {
    // set parent navigation type for toolbar flyout links
    this.toolbarChilds.forEach((toolbarChild) => {
      toolbarChild.querySelectorAll("eon-ui-navigation-flyout-link").forEach((link) => {
        link.dataset.parentNavigationType = "toolbar";
      });
    });

    // set parent navigation type for main flyout links
    this.mainChilds.forEach((mainChild) => {
      mainChild.querySelectorAll("eon-ui-navigation-flyout-link").forEach((link) => {
        link.dataset.parentNavigationType = "main";
      });
    });
  }

  setInnerBackgroundToSubs() {
    this.toolbarChilds.forEach((child: HTMLElement) => {
      child.setAttribute("inner-background", NAVIGATION_TOOLBAR_FLYOUT_INNER_BACKGROUND);
    });

    this.mainChilds.forEach((child: HTMLElement) => {
      child.setAttribute("inner-background", NAVIGATION_MAIN_FLYOUT_INNER_BACKGROUND);
    });
  }

  showToolbarSub() {
    this.toolbarChilds.forEach((element: HTMLElement) => {
      element.style.display = "none";
    });

    this.activeToolbarSubContainer.style.display = "block";
    this.setToolbarSubHeight();
  }

  showMainSub() {
    this.mainChilds.forEach((element: HTMLElement) => {
      element.style.display = "none";
    });

    this.activeMainSubContainer.style.display = "block";

    this.setMainSubHeight();
  }

  showMobileSub() {
    this.activeMobileSubContainer.classList.add("act");
    this.mobileMainNavigation.classList.add("scale");
  }

  showMobileSubImmediately() {
    this.activeMobileSubContainer.classList.add("no-animation");
    this.activeMobileSubContainer.classList.add("act");
    this.mobileMainNavigation.classList.add("scale");
  }

  initSubKeyboardNav(activeSubContainer: HTMLElement) {
    let subItems = Array.prototype.slice.call(activeSubContainer.querySelectorAll("*"));
    let firstFocusableItem: HTMLElement;
    let lastFocusableItem: HTMLElement;

    subItems.forEach((element) => {
      if (element.hasAttribute("tabindex")) {
        if (!firstFocusableItem) firstFocusableItem = element;
        lastFocusableItem = element;
      } else {
        if (element.shadowRoot) {
          let focusableElement = element.shadowRoot.querySelector("[tabindex]");
          if (!!focusableElement) {
            if (!firstFocusableItem) firstFocusableItem = focusableElement;
            lastFocusableItem = focusableElement;
          }
        }
      }
    });

    if (!!firstFocusableItem) firstFocusableItem.focus();
    if (!!firstFocusableItem && !!lastFocusableItem)
      this.exitSecondaryNavViaKey(firstFocusableItem, lastFocusableItem, activeSubContainer);
  }

  exitSecondaryNavViaKey(firstChild: HTMLElement, lastChild: HTMLElement, activeSubContainer: HTMLElement) {
    if (!firstChild.classList.contains("event-listener")) {
      firstChild.classList.add("event-listener");
      firstChild.addEventListener("keydown", (e) => {
        switch (e.key) {
          case KeyIds.TAB:
            if (e.shiftKey) {
              e.preventDefault();
              this.closeMainSub();
              let relatedLink = document.querySelector(
                `[sub-nav-rel="${activeSubContainer.getAttribute("id")}"]`
              ) as HTMLElement;
              (relatedLink.shadowRoot.querySelector("[tabindex]") as HTMLElement)?.focus();
            }
            break;
          default:
            break;
        }
      });
    }

    if (!lastChild.classList.contains("event-listener")) {
      lastChild.classList.add("event-listener");
      lastChild.addEventListener("keydown", (e) => {
        switch (e.key) {
          case KeyIds.TAB:
            if (!e.shiftKey) {
              e.preventDefault();
              this.closeMainSub();
              let relatedLink = document.querySelector(
                `[sub-nav-rel="${this.activeMainSubContainer.getAttribute("id")}"]`
              ) as HTMLElement;
              (relatedLink.shadowRoot.querySelector("[tabindex]") as HTMLElement)?.focus();
            }
            break;
          default:
            break;
        }
      });
    }
  }

  getNavigationStructure() {
    this.hasMetaNavigation = !!this.hostElement.querySelector('[slot="meta"]');
    this.hasToolbarNavigation = !!this.hostElement.querySelector('[slot="toolbar"]');
    this.hasMainNavigation = !!this.hostElement.querySelector('[slot="main"]');
    this.hasExtension = !!this.hostElement.querySelector('[slot="extension"]');
  }

  @Watch("sticky")
  @Watch("stickyLevel")
  calcMobileNavigationPadding() {
    this.hasMetaNavigation && (this.mobileNavigationPadding += this.metaHeight);
    // this.hasToolbarNavigation && (mobileNavigationPadding += this.toolbarHeight);
    this.hasMainNavigation && (this.mobileNavigationPadding += this.mainHeight);

    switch (this.stickyLevel) {
      case NAVIGATION_STICKYSLEVEL_META:
        this.mobileNavigationPaddingSticky = this.mobileNavigationPadding;
        break;
      case NAVIGATION_STICKYSLEVEL_TOOLBAR:
        this.mobileNavigationPaddingSticky =
          this.mobileNavigationPadding - (this.hasMetaNavigation ? this.metaHeight : 0);
        break;
      case NAVIGATION_STICKYSLEVEL_MAIN:
        this.mobileNavigationPaddingSticky =
          /* mobileNavigationPadding -
          (this.hasMetaNavigation ? this.metaHeight : 0) -
          (this.hasToolbarNavigation ? this.toolbarHeight : 0); */
          this.mobileNavigationPadding - (this.hasMetaNavigation ? this.metaHeight : 0);
        break;
    }
  }

  setCssVariables(): void {
    setCssVariable(this.hostElement, "--mobileNavigationPadding", `${this.mobileNavigationPadding}px`);
    setCssVariable(this.hostElement, "--mobileNavigationPaddingSticky", `${this.mobileNavigationPaddingSticky}px`);
  }

  setToolbarSubHeight() {
    this.toolbarSubHeight = this.activeToolbarSubContainer.getBoundingClientRect().height + 64; // 64: padding-top and -bottom of parent container + space
  }

  setMainSubHeight() {
    this.mainSubHeight = this.activeMainSubContainer.getBoundingClientRect().height + 64 + 10; // 64: padding-top and -bottom of parent container + space
  }

  deactivateMainLinks() {
    this.mainLinks.forEach((item: HTMLEonUiNavigationMainLinkElement) => {
      item.removeActiveState();
    });
  }

  deactivateMainIconLinks() {
    this.mainIconLinks.forEach((item: HTMLEonUiNavigationMainIconLinkElement) => {
      item.removeActiveState();
    });
  }

  deactivateToolbarLinks() {
    this.toolbarLinks.forEach((item: HTMLEonUiNavigationToolbarIconLinkElement) => {
      item.removeActiveState();
    });
  }

  initMainSubFunctionality() {
    /*
     * Init secondary navigation mouse leave function
     * Closes secondary navigation after mouse leave at the bottom (400ms)
     */
    this.mainSubWrapper.addEventListener("mouseleave", (mouse) => {
      if (this.closestEdge(mouse, this.mainSubWrapper) !== "top") {
        this.mouseLeaveTimeout = setTimeout(() => {
          this.closeMainSub();
        }, 400);
      }
    });

    /*
     * Init secondary navigation mouse enter function
     * Interrupts mouse leave function if reentering navigation (400ms)
     */
    this.mainSubWrapper.addEventListener("mouseenter", () => {
      if (this.mouseLeaveTimeout) {
        clearTimeout(this.mouseLeaveTimeout);
      }
    });
  }

  /**
   * Loop through the columns which should be available for mobile navigation
   * and append them to the mobile navigation div
   */
  private createMainMobileNavigation() {
    if (this.hasMainNavigation) {
      // this.mainNavigation = this.hostElement.querySelector('[slot="main"]');

      if (this.mainNavigation.hasAttribute("burger-position")) {
        this.burgerPosition = this.mainNavigation.getAttribute("burger-position") as BurgerPosition;
      }

      this.mobileWrapper.classList.add(`from-${this.burgerPosition}`);
    }
  }

  private createMobileMain() {
    const mainNavigationChilds: HTMLElement[] = [];
    const toolbarNavigationChilds: HTMLElement[] = [];

    this.mainNavigation?.querySelectorAll("eon-ui-navigation-main-link")?.forEach((element) => {
      const item = element.cloneNode(true) as HTMLElement;
      item.setAttribute("mobile", "true");
      mainNavigationChilds.push(item);
    });

    this.toolbarNavigation?.querySelectorAll("eon-ui-navigation-toolbar-icon-link")?.forEach((element) => {
      const item = element.cloneNode(true) as HTMLElement;
      item.setAttribute("mobile", "true");
      toolbarNavigationChilds.push(item);
    });

    return (
      <div class="mobile-main-nav" ref={(el) => (this.mobileMainNavigation = el as HTMLElement)}>
        <div class="mobile-main-nav-inner">
          <div
            class="main-navigation-links"
            innerHTML={mainNavigationChilds.map((element: HTMLElement) => element.outerHTML).join("")}
          ></div>
          <div
            class="toolbar-navigation-links"
            innerHTML={toolbarNavigationChilds.map((element: HTMLElement) => element.outerHTML).join("")}
          ></div>
        </div>
      </div>
    );
  }

  /**
   * Creates the submenu for the navigation
   */
  private createMobileSub(subNavi, relatedNavigation) {
    let subNavigationChilds: HTMLElement[] = [];
    const backLinkText = relatedNavigation.querySelector(`[sub-nav-rel='${subNavi.id}']`)?.getAttribute("text");

    subNavi.querySelectorAll("eon-ui-grid-control-column").forEach((column) => {
      Array.from(column.children).forEach((columnItem: HTMLElement) => {
        const item = columnItem.cloneNode(true) as HTMLElement;
        item.setAttribute("mobile", "true");
        subNavigationChilds.push(item);
      });
    });

    return (
      <div class="mobile-secondary-nav" id={`mobile-${subNavi.id}`}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div class="mobile-secondary-nav-backlink" onClick={() => this.closeMobileSub()}>
          <span>{sanitizeHtml(backLinkText)}</span>
          <eon-ui-icon name="chevron_small_left"></eon-ui-icon>
        </div>
        <div
          class="mobile-secondary-nav-inner"
          innerHTML={subNavigationChilds.map((element: HTMLElement) => element.outerHTML).join("")}
        ></div>
      </div>
    );
  }

  /**
   * Creates the submenu for the navigation
   */
  private createMobileSubWithGrid(subNavi: HTMLElement, relatedNavigation) {
    const backLinkText = relatedNavigation.querySelector(`[sub-nav-rel='${subNavi.id}']`)?.getAttribute("text");

    const subNaviClone = document.importNode(subNavi, true);
    subNaviClone.removeAttribute("sub-nav-rel");
    subNaviClone.setAttribute("force-mobile-layout", "true");
    subNaviClone.style.display = "block";

    return (
      <div class="mobile-secondary-nav" id={`mobile-${subNavi.id}`}>
        {!!backLinkText && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
          <div class="mobile-secondary-nav-backlink" onClick={() => this.closeMobileSub()}>
            <span>{backLinkText}</span>
            <eon-ui-icon name="chevron_small_left"></eon-ui-icon>
          </div>
        )}
        <div
          class={{ "mobile-secondary-nav-inner": true, spacing: true, "spacing-top": !backLinkText }}
          innerHTML={subNaviClone.outerHTML}
        ></div>
      </div>
    );
  }

  closestEdge(mouse, elem) {
    var elemBounding = elem.getBoundingClientRect();

    var elementLeftEdge = elemBounding.left;
    var elementTopEdge = elemBounding.top;
    var elementRightEdge = elemBounding.right;
    var elementBottomEdge = elemBounding.bottom;

    var mouseX = mouse.pageX;
    var mouseY = mouse.pageY;

    var topEdgeDist = Math.abs(elementTopEdge - mouseY);
    var bottomEdgeDist = Math.abs(elementBottomEdge - mouseY);
    var leftEdgeDist = Math.abs(elementLeftEdge - mouseX);
    var rightEdgeDist = Math.abs(elementRightEdge - mouseX);

    var min = Math.min(topEdgeDist, bottomEdgeDist, leftEdgeDist, rightEdgeDist);

    switch (min) {
      case leftEdgeDist:
        return "left";
      case rightEdgeDist:
        return "right";
      case topEdgeDist:
        return "top";
      case bottomEdgeDist:
        return "bottom";
    }
  }

  render() {
    return (
      <Host id="eonui-navigation">
        <nav
          aria-label="Navigation"
          class={pfx({
            "navigation-wrapper": true,
            [`nav${!!this.hasMetaNavigation ? "-meta" : ""}${!!this.hasToolbarNavigation ? "-toolbar" : ""}${
              !!this.hasMainNavigation ? "-main" : ""
            }`]: true,
            [`sticky`]: !!this.sticky,
            [`sticky-level-${this.stickyLevel}`]: !!this.sticky && !!this.stickyLevel,
            [`collapsed`]: !!this.sticky && this.currentScrollDirection === "down"
          })}
          ref={(el) => (this.navigationWrapper = el as HTMLElement)}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            class={pfx({ overlay: true, act: !!this.isMobileNavOpen })}
            onClick={() => this.closeMobileNav()}
            onTouchEnd={() => this.closeMobileNav()}
          ></div>
          {this.hasMetaNavigation && (
            <div class={pfx("meta-wrapper")}>
              <slot name="meta" />
            </div>
          )}
          {this.hasToolbarNavigation && (
            <Fragment>
              <div class={pfx("toolbar-wrapper")}>
                <slot name="toolbar" />
              </div>
              <div
                class={pfx({ "toolbar-sub": true, active: this.isToolbarSubOpen })}
                style={{
                  height: `${this.isToolbarSubOpen && this.toolbarSubHeight ? this.toolbarSubHeight : 0}px`
                }}
              >
                <div class={pfx("toolbar-sub-inner")}>
                  <slot name="toolbar-sub" />
                </div>
              </div>
            </Fragment>
          )}
          {this.hasMainNavigation && (
            <Fragment>
              <div class={pfx("main-wrapper")}>
                <slot name="main" />
              </div>
              <div
                ref={(el) => (this.mainSubWrapper = el as HTMLElement)}
                class={pfx("main-sub-wrapper")}
                style={{
                  height: `${this.isMainSubWrapperOpen && this.mainSubHeight ? this.mainSubHeight : 0}px`
                }}
              >
                <div
                  class={pfx({
                    "main-sub": true,
                    active: this.isMainSubOpen
                  })}
                >
                  <div class={pfx("main-sub-inner")}>
                    <slot name="main-sub" />
                    <slot name="main-icon-sub" />
                  </div>
                </div>
              </div>

              <div
                class={pfx("mobile-wrapper")}
                ref={(el) => (this.mobileWrapper = el as HTMLElement)}
                inner-background={NAVIGATION_MOBILE_FLYOUT_INNER_BACKGROUND}
              >
                {this.createMobileMain()}
                {Array.from(this.mainStandardChilds).map((child: HTMLElement) =>
                  this.createMobileSub(child, this.mainNavigation)
                )}
                {Array.from(this.mainIconChilds).map((child: HTMLElement) =>
                  this.createMobileSubWithGrid(child, this.mainNavigation)
                )}
                {Array.from(this.toolbarChilds).map((child: HTMLElement) =>
                  this.createMobileSubWithGrid(child, this.toolbarNavigation)
                )}
              </div>
            </Fragment>
          )}
          {this.hasExtension && (
            <div class={pfx("extension-wrapper")}>
              <slot name="extension" />
            </div>
          )}
        </nav>
      </Host>
    );
  }
}
